import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Plus, Trash2, Mail, MessageSquare, Loader2, Clock, Users,
  Send, Edit, X, PlayCircle, FileText, Upload, Shield, Gift, Search
} from 'lucide-react';
import { API } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';

const adminInputClass =
  'w-full bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed';
const adminTextareaClass =
  'w-full min-h-[120px] bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed';
const adminSelectTriggerClass =
  'w-full bg-misel-card border border-white/15 text-white rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed data-[placeholder]:text-gray-500';
const adminSelectContentClass =
  'z-[9999] bg-[#111111] border border-white/15 text-white shadow-xl';
const adminSelectItemClass =
  'text-white focus:bg-primary/20 focus:text-white';

const tabs = [
  ['campaigns', 'Campaigns'],
  ['automations', 'Automations'],
  ['templates', 'Templates'],
  ['contacts', 'Contacts'],
  ['import', 'Import Contacts'],
  ['direct', 'Direct Message'],
  ['suppressions', 'Suppression List'],
  ['namedays', 'Namedays'],
  ['logs', 'Delivery Logs']
];

const emptyCampaign = {
  id: null,
  name: '',
  channel: 'email',
  subject: '',
  heading: '',
  body: '',
  email_html: '',
  email_text: '',
  sms_text: '',
  hero_image_url: '',
  cta_label: '',
  cta_url: '',
  target_type: 'all_users',
  segment_id: '',
  user_id: '',
  contact_id: '',
  tag: '',
  scheduled_at: '',
  status: 'scheduled'
};

const emptyAutomation = {
  id: null,
  name: '',
  description: '',
  template_id: '',
  channel: 'email',
  automation_type: 'one_time',
  status: 'draft',
  target_type: 'all_contacts',
  subject: '',
  email_html: '',
  email_text: '',
  sms_text: '',
  sms_footer: '',
  heading: '',
  body: '',
  cta_label: '',
  cta_url: '',
  hero_image_url: '',
  scheduled_at: '',
  timezone: 'Europe/Athens',
  trigger_type: 'manual_trigger',
  tag: '',
  segment_id: '',
  user_id: '',
  contact_id: '',
  trigger_config: { days_before: 0, send_time: '10:00' },
  recurrence_rule: { frequency: 'weekly' }
};

const emptyTemplate = {
  id: null,
  name: '',
  channel: 'email',
  subject: '',
  heading: '',
  body: '',
  email_html: '',
  email_text: '',
  sms_text: '',
  sms_footer: '',
  hero_image_url: '',
  cta_label: '',
  cta_url: '',
  category: ''
};

const emptyContact = {
  id: null,
  first_name: '',
  last_name: '',
  full_name: '',
  email: '',
  phone: '',
  tags: '',
  nameday_name: '',
  nameday_month: '',
  nameday_day: '',
  nameday_confirmed: false,
  birthday_month: '',
  birthday_day: '',
  birthday_year: '',
  notes: '',
  email_consent: true,
  sms_consent: true,
  marketing_consent: true
};

const emptySuppression = {
  channel: 'all',
  email: '',
  phone: '',
  user_id: '',
  contact_id: '',
  reason: ''
};

const emptyNameday = {
  id: null,
  canonical_name: '',
  names: '',
  label_el: '',
  label_en: '',
  rule_type: 'fixed_date',
  month: '',
  day: '',
  easter_offset: '',
  notes: ''
};

const emptyDirect = {
  channel: 'email',
  recipient_type: 'user',
  user_id: '',
  contact_id: '',
  subject: '',
  email_html: '',
  email_text: '',
  sms_text: ''
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  return String(value).slice(0, 16);
};

const estimateSmsParts = (text = '') => {
  const gsm = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
  const encoding = String(text).split('').every((char) => gsm.includes(char)) ? 'gsm' : 'unicode';
  const chars = String(text).length;
  const single = encoding === 'gsm' ? 160 : 70;
  const multi = encoding === 'gsm' ? 153 : 67;
  return { encoding, chars, parts: chars <= single ? 1 : Math.max(1, Math.ceil(chars / multi)) };
};

const campaignName = (campaign) => (
  campaign.name || campaign.subject || campaign.heading || campaign.sms_text || 'Campaign'
);

const recipientLabel = (item) => item?.full_name || item?.name || item?.email || item?.phone || item?.id;

function TargetFields({
  value,
  setValue,
  users,
  contacts,
  segments,
  recipientLabelFn,
  inputClass,
  selectTriggerClass,
  selectContentClass,
  selectItemClass,
  forcedTriggered = false
}) {
  if (forcedTriggered) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-black/30 border border-white/10 rounded-sm px-3 py-2 text-gray-200">Triggered user</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Select value={value.target_type} onValueChange={(target_type) => setValue({ ...value, target_type })}>
        <SelectTrigger className={selectTriggerClass}><SelectValue /></SelectTrigger>
        <SelectContent className={selectContentClass}>
          <SelectItem className={selectItemClass} value="all_users">All users</SelectItem>
          <SelectItem className={selectItemClass} value="all_contacts">All contacts</SelectItem>
          <SelectItem className={selectItemClass} value="segment">Segment</SelectItem>
          <SelectItem className={selectItemClass} value="user">One user</SelectItem>
          <SelectItem className={selectItemClass} value="contact">One contact</SelectItem>
          <SelectItem className={selectItemClass} value="tag">Tag/imported list</SelectItem>
          <SelectItem className={selectItemClass} value="triggered_user">Triggered user</SelectItem>
        </SelectContent>
      </Select>
      {value.target_type === 'segment' && (
        <Select value={value.segment_id || undefined} onValueChange={(segment_id) => setValue({ ...value, segment_id })}>
          <SelectTrigger className={selectTriggerClass}><SelectValue placeholder="Select segment" /></SelectTrigger>
          <SelectContent className={selectContentClass}>
            {segments.map((segment) => <SelectItem className={selectItemClass} key={segment.id} value={segment.id}>{segment.name_el || segment.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {value.target_type === 'user' && (
        <Select value={value.user_id || undefined} onValueChange={(user_id) => setValue({ ...value, user_id })}>
          <SelectTrigger className={selectTriggerClass}><SelectValue placeholder="Select user" /></SelectTrigger>
          <SelectContent className={selectContentClass}>
            {users.map((user) => <SelectItem className={selectItemClass} key={user.id} value={user.id}>{recipientLabelFn(user)}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {value.target_type === 'contact' && (
        <Select value={value.contact_id || undefined} onValueChange={(contact_id) => setValue({ ...value, contact_id })}>
          <SelectTrigger className={selectTriggerClass}><SelectValue placeholder="Select contact" /></SelectTrigger>
          <SelectContent className={selectContentClass}>
            {contacts.map((contact) => <SelectItem className={selectItemClass} key={contact.id} value={contact.id}>{recipientLabelFn(contact)}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {(value.target_type === 'tag' || value.target_type === 'imported_list') && (
        <Input className={inputClass} value={value.tag || ''} onChange={(e) => setValue({ ...value, tag: e.target.value })} placeholder="Tag" />
      )}
    </div>
  );
}

function AutomationTemplatePicker({
  automationForm,
  templates,
  setAutomationForm,
  inputClass,
  selectTriggerClass,
  selectContentClass,
  selectItemClass
}) {
  const filteredTemplates = templates.filter((template) => template.channel === automationForm.channel);
  const selectedTemplate = templates.find((template) => template.id === automationForm.template_id);
  const applyTemplate = () => {
    if (!selectedTemplate || selectedTemplate.channel !== automationForm.channel) return;
    setAutomationForm((prev) => ({
      ...prev,
      template_id: selectedTemplate.id,
      channel: selectedTemplate.channel,
      subject: selectedTemplate.subject || '',
      email_html: selectedTemplate.email_html || '',
      email_text: selectedTemplate.email_text || '',
      sms_text: selectedTemplate.sms_text || '',
      sms_footer: selectedTemplate.sms_footer || '',
      hero_image_url: selectedTemplate.hero_image_url || '',
      heading: selectedTemplate.heading || '',
      body: selectedTemplate.body || '',
      cta_label: selectedTemplate.cta_label || '',
      cta_url: selectedTemplate.cta_url || ''
    }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Select
        value={automationForm.template_id || undefined}
        onValueChange={(template_id) => setAutomationForm((prev) => ({ ...prev, template_id }))}
      >
        <SelectTrigger className={selectTriggerClass}><SelectValue placeholder="Select template" /></SelectTrigger>
        <SelectContent className={selectContentClass}>
          {filteredTemplates.map((template) => (
            <SelectItem className={selectItemClass} key={template.id} value={template.id}>{template.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button type="button" variant="outline" className="border-white/20" onClick={applyTemplate} disabled={!selectedTemplate || selectedTemplate.channel !== automationForm.channel}>
        Apply Template
      </Button>
      <Input className={inputClass} value={selectedTemplate ? `${selectedTemplate.name} (${selectedTemplate.channel})` : 'No template selected'} readOnly />
    </div>
  );
}

function MessageFields({
  value,
  setValue,
  onChannelChange,
  smsParts,
  smsConfigured,
  inputClass,
  textareaClass,
  selectTriggerClass,
  selectContentClass,
  selectItemClass
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Select value={value.channel} onValueChange={(channel) => (onChannelChange ? onChannelChange(channel) : setValue({ ...value, channel }))}>
          <SelectTrigger className={selectTriggerClass}><SelectValue /></SelectTrigger>
          <SelectContent className={selectContentClass}>
            <SelectItem className={selectItemClass} value="email">Email</SelectItem>
            <SelectItem className={selectItemClass} value="sms" disabled={!smsConfigured}>SMS</SelectItem>
          </SelectContent>
        </Select>
        {value.channel === 'email' && <Input className={inputClass} value={value.subject || ''} onChange={(e) => setValue({ ...value, subject: e.target.value })} placeholder="Subject" />}
        {value.channel === 'email' && <Input className={inputClass} value={value.hero_image_url || ''} onChange={(e) => setValue({ ...value, hero_image_url: e.target.value })} placeholder="Hero image URL" />}
      </div>
      {value.channel === 'email' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Input className={inputClass} value={value.heading || ''} onChange={(e) => setValue({ ...value, heading: e.target.value })} placeholder="Heading" />
            <textarea className={textareaClass} value={value.body || ''} onChange={(e) => setValue({ ...value, body: e.target.value })} placeholder="Body text. Use {{name}}, {{email}}, {{phone}}" />
            <div className="grid grid-cols-2 gap-3">
              <Input className={inputClass} value={value.cta_label || ''} onChange={(e) => setValue({ ...value, cta_label: e.target.value })} placeholder="CTA label" />
              <Input className={inputClass} value={value.cta_url || ''} onChange={(e) => setValue({ ...value, cta_url: e.target.value })} placeholder="CTA URL" />
            </div>
            <textarea className={textareaClass} value={value.email_html || ''} onChange={(e) => setValue({ ...value, email_html: e.target.value })} placeholder="Optional custom HTML" />
          </div>
          <div className="bg-black/30 border border-white/10 rounded-sm p-4">
            <p className="text-sm text-gray-400 mb-3">Email preview</p>
            <div className="bg-white rounded-sm overflow-hidden p-5 text-black">
              {value.hero_image_url && <img src={value.hero_image_url} alt="" className="w-full max-h-40 object-cover mb-4" />}
              <h2 className="font-bold text-xl mb-2">{value.heading || value.subject || 'Heading'}</h2>
              <p className="whitespace-pre-line text-gray-700">{value.body || value.email_text || 'Email text preview'}</p>
              {value.cta_label && <p className="inline-block mt-4 bg-red-700 text-white px-4 py-2 rounded-sm">{value.cta_label}</p>}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <textarea className={textareaClass} value={value.sms_text || ''} onChange={(e) => setValue({ ...value, sms_text: e.target.value })} placeholder="SMS text. Use {{name}}" disabled={!smsConfigured} />
          <p className="text-xs text-gray-500 mt-1">
            {smsParts.chars} chars, {smsParts.encoding}, estimated {smsParts.parts} SMS part(s). Sender: MISEL GROUP.
          </p>
          <p className="text-xs text-gray-500">Recommendation: include opt-out language where appropriate.</p>
        </div>
      )}
    </div>
  );
}

export default function CampaignsManagement() {
  const { i18n } = useTranslation();
  const isEl = i18n.language === 'el';
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [activeTab, setActiveTab] = useState('campaigns');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [namedays, setNamedays] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(emptyCampaign);
  const [automationForm, setAutomationForm] = useState(emptyAutomation);
  const [templateForm, setTemplateForm] = useState(emptyTemplate);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [suppressionForm, setSuppressionForm] = useState(emptySuppression);
  const [namedayForm, setNamedayForm] = useState(emptyNameday);
  const [directForm, setDirectForm] = useState(emptyDirect);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [selectedLogs, setSelectedLogs] = useState(null);
  const [preview, setPreview] = useState(null);
  const [runDueResult, setRunDueResult] = useState(null);
  const [namedaySearch, setNamedaySearch] = useState('');
  const [namedaySearchResult, setNamedaySearchResult] = useState(null);

  const providers = stats?.providers || {};
  const providerStatusLoaded = Boolean(stats?.providers);
  const emailProvider = providers.email || {};
  const smsProvider = providers.sms || {};
  const emailUnavailable = providerStatusLoaded && emailProvider.configured === false;
  const smsUnavailable = providerStatusLoaded && smsProvider.configured === false;
  const smsConfigured = !smsUnavailable;
  const emailProviderName = emailProvider.provider || (providerStatusLoaded ? 'none' : 'checking');
  const emailProviderLabel = emailProviderName === 'resend' ? 'Resend' : emailProviderName;
  const smsProviderName = smsProvider.provider || (providerStatusLoaded ? 'none' : 'checking');
  const smsProviderLabel = smsProviderName === 'yuboto' ? 'Yuboto' : smsProviderName;
  const campaignSms = estimateSmsParts(form.sms_text);
  const automationSms = estimateSmsParts(automationForm.sms_text);
  const directSms = estimateSmsParts(directForm.sms_text);
  const targetFieldProps = {
    users,
    contacts,
    segments,
    recipientLabelFn: recipientLabel,
    inputClass: adminInputClass,
    selectTriggerClass: adminSelectTriggerClass,
    selectContentClass: adminSelectContentClass,
    selectItemClass: adminSelectItemClass
  };
  const messageFieldProps = {
    smsConfigured,
    inputClass: adminInputClass,
    textareaClass: adminTextareaClass,
    selectTriggerClass: adminSelectTriggerClass,
    selectContentClass: adminSelectContentClass,
    selectItemClass: adminSelectItemClass
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const safeSet = (setter, fallback) => (result) => {
    if (result.status === 'fulfilled') setter(result.value.data || fallback);
  };

  const fetchData = async () => {
    setLoading(true);
    const calls = await Promise.allSettled([
      axios.get(`${API}/admin/campaigns/scheduled`, { headers }),
      axios.get(`${API}/admin/messaging/segments`, { headers }),
      axios.get(`${API}/admin/campaigns/stats`, { headers }),
      axios.get(`${API}/admin/users`, { headers }),
      axios.get(`${API}/admin/messaging/contacts`, { headers }),
      axios.get(`${API}/admin/messaging/templates`, { headers }),
      axios.get(`${API}/admin/messaging/automations`, { headers }),
      axios.get(`${API}/admin/messaging/suppressions`, { headers }),
      axios.get(`${API}/admin/namedays`, { headers }),
      axios.get(`${API}/admin/messaging/delivery-logs`, { headers })
    ]);
    [
      safeSet(setCampaigns, []),
      safeSet(setSegments, []),
      safeSet(setStats, null),
      safeSet(setUsers, []),
      safeSet(setContacts, []),
      safeSet(setTemplates, []),
      safeSet(setAutomations, []),
      safeSet(setSuppressions, []),
      safeSet(setNamedays, []),
      safeSet(setLogs, [])
    ].forEach((handler, index) => handler(calls[index]));
    setLoading(false);
  };

  const saveCampaign = async (e) => {
    e.preventDefault();
    if (form.channel === 'sms' && smsUnavailable) {
      toast.error('SMS provider is not configured');
      return;
    }
    if (form.channel === 'email' && emailUnavailable) {
      toast.error('Email provider is not configured');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.id) {
        await axios.put(`${API}/admin/campaigns/scheduled/${form.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/campaigns/scheduled`, payload, { headers });
      }
      toast.success('Campaign saved');
      setForm(emptyCampaign);
      fetchData();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Campaign save failed');
    } finally {
      setSaving(false);
    }
  };

  const editCampaign = (campaign) => {
    setForm({ ...emptyCampaign, ...campaign, scheduled_at: toDateTimeLocal(campaign.scheduled_at), channel: campaign.channel || campaign.type || 'email' });
    setActiveTab('campaigns');
  };

  const cancelCampaign = async (campaign) => {
    if (!window.confirm('Cancel this campaign?')) return;
    await axios.delete(`${API}/admin/campaigns/scheduled/${campaign.id}`, { headers });
    toast.success('Campaign cancelled');
    fetchData();
  };

  const previewCampaign = async (campaign) => {
    const res = await axios.get(`${API}/admin/campaigns/scheduled/${campaign.id}/preview-recipients`, { headers });
    setPreview({ title: campaignName(campaign), ...res.data });
  };

  const fetchCampaignLogs = async (campaign) => {
    const res = await axios.get(`${API}/admin/campaigns/scheduled/${campaign.id}/logs`, { headers });
    setSelectedLogs({ title: campaignName(campaign), ...res.data });
  };

  const sendCampaignTest = async (campaign) => {
    const channel = campaign.channel || campaign.type || 'email';
    const value = window.prompt(channel === 'sms' ? 'Test phone number' : 'Test email address');
    if (!value) return;
    await axios.post(`${API}/admin/campaigns/scheduled/${campaign.id}/send-test`, channel === 'sms' ? { phone: value } : { email: value }, { headers });
    toast.success('Test send requested');
  };

  const runDueCampaigns = async () => {
    try {
      const res = await axios.post(`${API}/admin/campaigns/run-due`, {}, { headers });
      setRunDueResult({ title: 'Run Due results', ...res.data });
      if ((res.data.processed || 0) === 0) {
        toast.info('No due campaigns were processed. Check status and schedule.');
      } else {
        toast.success(`${res.data.processed || 0} campaigns processed`);
      }
      fetchData();
      return res.data;
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Run due failed');
      throw e2;
    }
  };

  const sendCampaignNow = async (campaign) => {
    try {
      const payload = {
        ...campaign,
        scheduled_at: new Date().toISOString(),
        status: 'scheduled'
      };
      await axios.put(`${API}/admin/campaigns/scheduled/${campaign.id}`, payload, { headers });
      const result = await runDueCampaigns();
      setRunDueResult({ title: `Send now: ${campaignName(campaign)}`, ...result });
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Send now failed');
    }
  };

  const saveAutomation = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...automationForm,
        target_type: automationForm.automation_type === 'event_triggered' && automationForm.trigger_type === 'user_registered'
          ? 'triggered_user'
          : automationForm.target_type
      };
      if (automationForm.id) {
        await axios.put(`${API}/admin/messaging/automations/${automationForm.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/messaging/automations`, payload, { headers });
      }
      toast.success('Automation saved');
      setAutomationForm(emptyAutomation);
      fetchData();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Automation save failed');
    } finally {
      setSaving(false);
    }
  };

  const automationAction = async (automation, action) => {
    const res = await axios.post(`${API}/admin/messaging/automations/${automation.id}/${action}`, {}, { headers });
    toast.success(action === 'run-now' ? `Run processed ${res.data.sent || 0} sends` : `Automation ${action}`);
    fetchData();
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    const payload = { ...templateForm };
    if (templateForm.id) {
      await axios.put(`${API}/admin/messaging/templates/${templateForm.id}`, payload, { headers });
    } else {
      await axios.post(`${API}/admin/messaging/templates`, payload, { headers });
    }
    toast.success('Template saved');
    setTemplateForm(emptyTemplate);
    fetchData();
  };

  const loadTemplateIntoCampaign = (template) => {
    setForm({ ...form, ...template, id: form.id, name: form.name || template.name, channel: template.channel });
    setActiveTab('campaigns');
  };

  const isTriggeredUserRegistration = automationForm.automation_type === 'event_triggered' && automationForm.trigger_type === 'user_registered';

  const updateAutomationType = (automation_type) => {
    setAutomationForm((prev) => ({
      ...prev,
      automation_type,
      trigger_type: automation_type === 'nameday' ? 'nameday' : (automation_type === 'event_triggered' ? 'user_registered' : prev.trigger_type),
      target_type: automation_type === 'event_triggered' ? 'triggered_user' : (prev.target_type === 'triggered_user' ? 'all_contacts' : prev.target_type)
    }));
  };

  const updateAutomationTrigger = (trigger_type) => {
    setAutomationForm((prev) => ({
      ...prev,
      trigger_type,
      target_type: prev.automation_type === 'event_triggered' && trigger_type === 'user_registered' ? 'triggered_user' : prev.target_type
    }));
  };

  const updateAutomationChannel = (channel) => {
    setAutomationForm((prev) => {
      const selectedTemplate = templates.find((template) => template.id === prev.template_id);
      return {
        ...prev,
        channel,
        template_id: selectedTemplate && selectedTemplate.channel === channel ? prev.template_id : ''
      };
    });
  };

  const saveContact = async (e) => {
    e.preventDefault();
    const payload = {
      ...contactForm,
      tags: String(contactForm.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean),
      birthday_month: contactForm.birthday_month ? Number(contactForm.birthday_month) : null,
      birthday_day: contactForm.birthday_day ? Number(contactForm.birthday_day) : null,
      birthday_year: contactForm.birthday_year ? Number(contactForm.birthday_year) : null,
      nameday_month: contactForm.nameday_month ? Number(contactForm.nameday_month) : null,
      nameday_day: contactForm.nameday_day ? Number(contactForm.nameday_day) : null
    };
    if (contactForm.id) {
      await axios.put(`${API}/admin/messaging/contacts/${contactForm.id}`, payload, { headers });
    } else {
      await axios.post(`${API}/admin/messaging/contacts`, payload, { headers });
    }
    toast.success('Contact saved');
    setContactForm(emptyContact);
    fetchData();
  };

  const lookupContactNameday = async () => {
    if (!contactForm.nameday_name) return;
    const res = await axios.get(`${API}/admin/namedays/search?name=${encodeURIComponent(contactForm.nameday_name)}`, { headers });
    const options = res.data?.options || [];
    if (options.length === 1) {
      setContactForm({
        ...contactForm,
        nameday_month: options[0].month,
        nameday_day: options[0].day,
        nameday_confirmed: true,
        nameday_source: 'admin_selected'
      });
      toast.success('Nameday matched');
    } else {
      setNamedaySearchResult({ title: `Options for ${contactForm.nameday_name}`, ...res.data });
      toast.info(options.length ? 'Multiple nameday options found in Namedays tab result panel' : 'No nameday options found');
    }
  };

  const previewImport = async () => {
    const res = await axios.post(`${API}/admin/messaging/contacts/import-preview`, { csv_text: importText, source: 'admin_import' }, { headers });
    setImportPreview(res.data);
  };

  const commitImport = async () => {
    const res = await axios.post(`${API}/admin/messaging/contacts/import-commit`, { csv_text: importText, source: 'admin_import', update_existing: true }, { headers });
    toast.success(`Created ${res.data.created || 0}, updated ${res.data.updated || 0}`);
    setImportPreview(null);
    setImportText('');
    fetchData();
  };

  const saveSuppression = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/admin/messaging/suppressions`, suppressionForm, { headers });
    toast.success('Suppression added');
    setSuppressionForm(emptySuppression);
    fetchData();
  };

  const saveNameday = async (e) => {
    e.preventDefault();
    const payload = {
      ...namedayForm,
      names: String(namedayForm.names || '').split(',').map((name) => name.trim()).filter(Boolean),
      month: namedayForm.month ? Number(namedayForm.month) : null,
      day: namedayForm.day ? Number(namedayForm.day) : null,
      easter_offset: namedayForm.easter_offset !== '' ? Number(namedayForm.easter_offset) : null
    };
    if (namedayForm.id) {
      await axios.put(`${API}/admin/namedays/${namedayForm.id}`, payload, { headers });
    } else {
      await axios.post(`${API}/admin/namedays`, payload, { headers });
    }
    toast.success('Nameday saved');
    setNamedayForm(emptyNameday);
    fetchData();
  };

  const searchNamedays = async () => {
    if (!namedaySearch) return;
    const res = await axios.get(`${API}/admin/namedays/search?name=${encodeURIComponent(namedaySearch)}`, { headers });
    setNamedaySearchResult(res.data);
  };

  const seedStarterNamedays = async () => {
    const res = await axios.post(`${API}/admin/namedays/seed-starter`, {}, { headers });
    setNamedaySearchResult({ title: 'Greek namedays seed', ...res.data });
    const aliases = res.data.alias_created_count || 0;
    toast.success(`Greek namedays seed completed: ${res.data.created_count || 0} created, ${res.data.skipped_count || 0} skipped, ${aliases} aliases added.`);
    if (res.data.seed_source === 'fallback_starter') {
      toast.info('Fallback starter seed used. Full dataset files are missing.');
    }
    fetchData();
  };

  const seedDatasetNamedays = async () => {
    try {
      const res = await axios.post(`${API}/admin/namedays/seed-dataset`, {}, { headers });
      setNamedaySearchResult({ title: 'Greek namedays dataset seed', ...res.data });
      toast.success(`Greek namedays seed completed: ${res.data.created_count || 0} created, ${res.data.skipped_count || 0} skipped, ${res.data.alias_created_count || 0} aliases added.`);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Dataset seed failed');
    }
    fetchData();
  };

  const importMasterNamedays = async () => {
    const csvText = window.prompt('Paste master nameday CSV data');
    if (!csvText) return;
    try {
      const res = await axios.post(`${API}/admin/namedays/import-master`, { csv_text: csvText }, { headers });
      setNamedaySearchResult({ title: 'Master nameday import', ...res.data });
      toast.success(`Master nameday import completed: ${res.data.created_count || 0} created, ${res.data.skipped_count || 0} skipped, ${res.data.alias_created_count || 0} aliases added.`);
      fetchData();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Master nameday import failed');
    }
  };

  const sendDirect = async (e) => {
    e.preventDefault();
    const payload = { ...directForm };
    if (payload.recipient_type === 'contact') payload.user_id = '';
    if (payload.recipient_type === 'user') payload.contact_id = '';
    await axios.post(`${API}/admin/messages/direct`, payload, { headers });
    toast.success('Direct message sent');
    setDirectForm(emptyDirect);
    fetchData();
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-300',
      scheduled: 'bg-blue-500/20 text-blue-400',
      executing: 'bg-yellow-500/20 text-yellow-400',
      partial: 'bg-orange-500/20 text-orange-300',
      completed: 'bg-green-500/20 text-green-400',
      active: 'bg-green-500/20 text-green-400',
      paused: 'bg-yellow-500/20 text-yellow-400',
      failed: 'bg-red-500/20 text-red-400',
      cancelled: 'bg-gray-500/20 text-gray-400',
      sent: 'bg-green-500/20 text-green-400',
      skipped: 'bg-gray-500/20 text-gray-400'
    };
    return <span className={`px-2 py-1 rounded text-xs ${styles[status] || styles.scheduled}`}>{status || '-'}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="text-primary" size={28} />
          <div>
            <h1 className="text-3xl font-bold text-white">{isEl ? 'Καμπάνιες & Κέντρο Μηνυμάτων' : 'Campaigns & Messaging Center'}</h1>
            <p className="text-sm text-gray-500">SMS, email, contacts, templates, automations, namedays, suppressions, and delivery logs.</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-white/20">Refresh Provider Status</Button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`px-3 py-2 rounded-sm text-sm border ${activeTab === key ? 'bg-primary border-primary text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:text-white'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`${!providerStatusLoaded ? 'bg-blue-500/10 border-blue-500/30 text-blue-200' : emailUnavailable ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200' : 'bg-green-500/10 border-green-500/30 text-green-200'} border rounded-sm p-4 text-sm`}>
          Email provider: {!providerStatusLoaded ? 'checking...' : `${emailProviderLabel} ${emailUnavailable ? 'not configured' : 'configured'}`}.
          {providerStatusLoaded && emailUnavailable && ' Email sends remain disabled until Resend API key and sender email are configured on the server.'}
          {providerStatusLoaded && !emailUnavailable && emailProvider.from_configured === false && ' Sender email is not configured.'}
        </div>
        <div className={`${!providerStatusLoaded ? 'bg-blue-500/10 border-blue-500/30 text-blue-200' : smsUnavailable ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200' : 'bg-green-500/10 border-green-500/30 text-green-200'} border rounded-sm p-4 text-sm`}>
          SMS provider: {!providerStatusLoaded ? 'checking...' : `${smsProviderLabel} ${smsUnavailable ? 'not configured' : 'configured'}`}.
          {providerStatusLoaded && smsUnavailable && ' SMS controls remain disabled and backend sends return 503.'}
          {providerStatusLoaded && smsProviderName === 'yuboto' && smsUnavailable && ' Yuboto needs endpoint, API key, and sender configured on the server.'}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      ) : (
        <>
          {activeTab === 'campaigns' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                {['total', 'scheduled', 'partial', 'completed', 'failed'].map((key) => (
                  <div key={key} className="bg-misel-card border border-white/10 rounded-sm p-4">
                    <p className="text-gray-400 text-sm capitalize">{key}</p>
                    <p className="text-2xl font-bold text-white">{stats?.[key] || 0}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={saveCampaign} className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">{form.id ? 'Edit campaign' : <span className="inline-flex items-center gap-2"><Plus size={18} />New campaign</span>}</h2>
                  {form.id && <Button type="button" variant="ghost" onClick={() => setForm(emptyCampaign)}><X size={16} /></Button>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input className={adminInputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Campaign name" />
                  <Input type="datetime-local" style={{ colorScheme: 'dark' }} className={adminInputClass} value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
                  <Select value={form.status} onValueChange={(status) => setForm({ ...form, status })}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      <SelectItem className={adminSelectItemClass} value="draft">Draft</SelectItem>
                      <SelectItem className={adminSelectItemClass} value="scheduled">Scheduled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" className="border-white/20" onClick={runDueCampaigns}><PlayCircle size={16} className="mr-2" />Run Due</Button>
                </div>
                <TargetFields value={form} setValue={setForm} {...targetFieldProps} />
                <MessageFields value={form} setValue={setForm} smsParts={campaignSms} {...messageFieldProps} />
                <Button type="submit" disabled={saving || (form.channel === 'sms' && smsUnavailable) || (form.channel === 'email' && emailUnavailable)} className="bg-primary hover:bg-primary/90">
                  {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                  Save Campaign
                </Button>
              </form>

              <div className="space-y-3">
                {campaigns.length === 0 ? <EmptyState icon={Calendar} title="No campaigns yet" /> : campaigns.map((campaign) => (
                  <div key={campaign.id} className="bg-misel-card border border-white/10 rounded-sm p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded ${campaign.channel === 'sms' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {campaign.channel === 'sms' ? <MessageSquare size={16} /> : <Mail size={16} />}
                      </div>
                      <div>
                        <p className="text-white font-medium">{campaignName(campaign)}</p>
                        <p className="text-sm text-gray-500">{campaign.target_type} · {campaign.scheduled_at ? new Date(campaign.scheduled_at).toLocaleString('el-GR') : 'No schedule'}</p>
                        <p className="text-xs text-gray-500">{campaign.stats?.sent || 0} sent / {campaign.stats?.failed || 0} failed / {campaign.stats?.skipped || 0} skipped</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {getStatusBadge(campaign.status)}
                      <Button variant="outline" size="sm" className="border-white/20" onClick={() => previewCampaign(campaign)}>Preview</Button>
                      <Button variant="outline" size="sm" className="border-white/20" onClick={() => fetchCampaignLogs(campaign)}>Logs</Button>
                      <Button variant="outline" size="sm" className="border-white/20" onClick={() => sendCampaignNow(campaign)}>Send now</Button>
                      <Button variant="outline" size="sm" className="border-white/20" onClick={() => sendCampaignTest(campaign)} disabled={(campaign.channel === 'sms' && smsUnavailable) || (campaign.channel === 'email' && emailUnavailable)}>Test</Button>
                      <Button variant="ghost" size="icon" onClick={() => editCampaign(campaign)}><Edit size={16} /></Button>
                      <Button variant="ghost" size="icon" onClick={() => cancelCampaign(campaign)} className="text-red-400"><Trash2 size={16} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'automations' && (
            <div className="space-y-6">
              <form onSubmit={saveAutomation} className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-5">
                <h2 className="text-xl font-bold text-white">{automationForm.id ? 'Edit automation' : 'New automation'}</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input className={adminInputClass} value={automationForm.name} onChange={(e) => setAutomationForm({ ...automationForm, name: e.target.value })} placeholder="Automation name" required />
                  <Select value={automationForm.automation_type} onValueChange={updateAutomationType}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      {['one_time', 'recurring', 'drip', 'event_triggered', 'nameday'].map((type) => <SelectItem className={adminSelectItemClass} key={type} value={type}>{type}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={automationForm.status} onValueChange={(status) => setAutomationForm({ ...automationForm, status })}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      {['draft', 'active', 'paused'].map((status) => <SelectItem className={adminSelectItemClass} key={status} value={status}>{status}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="datetime-local" style={{ colorScheme: 'dark' }} className={adminInputClass} value={automationForm.scheduled_at} onChange={(e) => setAutomationForm({ ...automationForm, scheduled_at: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Select value={automationForm.trigger_type || 'manual_trigger'} onValueChange={updateAutomationTrigger}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      {['manual_trigger', 'birthday', 'nameday', 'user_registered', 'subscription_created', 'subscription_expiring', 'seminar_ticket_purchased', 'video_completed', 'inactive_user'].map((trigger) => <SelectItem className={adminSelectItemClass} key={trigger} value={trigger}>{trigger}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className={adminInputClass} value={automationForm.trigger_config?.days_before ?? 0} onChange={(e) => setAutomationForm({ ...automationForm, trigger_config: { ...automationForm.trigger_config, days_before: Number(e.target.value || 0) } })} placeholder="Days before" type="number" />
                  <Input className={adminInputClass} value={automationForm.trigger_config?.send_time || '10:00'} onChange={(e) => setAutomationForm({ ...automationForm, trigger_config: { ...automationForm.trigger_config, send_time: e.target.value } })} placeholder="Send time" type="time" style={{ colorScheme: 'dark' }} />
                </div>
                <TargetFields value={{ ...automationForm, target_type: isTriggeredUserRegistration ? 'triggered_user' : automationForm.target_type }} setValue={setAutomationForm} forcedTriggered={isTriggeredUserRegistration} {...targetFieldProps} />
                <AutomationTemplatePicker automationForm={automationForm} templates={templates} setAutomationForm={setAutomationForm} inputClass={adminInputClass} selectTriggerClass={adminSelectTriggerClass} selectContentClass={adminSelectContentClass} selectItemClass={adminSelectItemClass} />
                <MessageFields value={automationForm} setValue={setAutomationForm} onChannelChange={updateAutomationChannel} smsParts={automationSms} {...messageFieldProps} />
                <Button type="submit" className="bg-primary hover:bg-primary/90">Save Automation</Button>
              </form>
              <CardsList items={automations} empty="No automations yet" render={(automation) => (
                <div key={automation.id} className="bg-misel-card border border-white/10 rounded-sm p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <p className="text-white font-medium">{automation.name}</p>
                    <p className="text-sm text-gray-500">{automation.automation_type} · {automation.channel} · next {automation.next_run_at || '-'}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {getStatusBadge(automation.status)}
                    <Button size="sm" variant="outline" className="border-white/20" onClick={() => setAutomationForm({ ...emptyAutomation, ...automation, scheduled_at: toDateTimeLocal(automation.scheduled_at) })}>Edit</Button>
                    <Button size="sm" variant="outline" className="border-white/20" onClick={() => automationAction(automation, 'run-now')}>Run now</Button>
                    <Button size="sm" variant="outline" className="border-white/20" onClick={() => automationAction(automation, automation.status === 'active' ? 'pause' : 'resume')}>{automation.status === 'active' ? 'Pause' : 'Resume'}</Button>
                  </div>
                </div>
              )} />
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="space-y-6">
              <form onSubmit={saveTemplate} className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-5">
                <h2 className="text-xl font-bold text-white">Templates</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input className={adminInputClass} value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="Template name" required />
                  <Input className={adminInputClass} value={templateForm.category} onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })} placeholder="Category" />
                </div>
                <MessageFields value={templateForm} setValue={setTemplateForm} smsParts={estimateSmsParts(templateForm.sms_text)} {...messageFieldProps} />
                <Button type="submit" className="bg-primary hover:bg-primary/90">Save Template</Button>
              </form>
              <CardsList items={templates} empty="No templates yet" render={(template) => (
                <div key={template.id} className="bg-misel-card border border-white/10 rounded-sm p-4 flex justify-between gap-4">
                  <div><p className="text-white">{template.name}</p><p className="text-sm text-gray-500">{template.channel} · {template.category || '-'}</p></div>
                  <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => loadTemplateIntoCampaign(template)}>Load</Button><Button size="sm" variant="ghost" onClick={() => setTemplateForm({ ...emptyTemplate, ...template })}><Edit size={16} /></Button></div>
                </div>
              )} />
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <form onSubmit={saveContact} className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-4">
                <h2 className="text-xl font-bold text-white">{contactForm.id ? 'Edit contact' : 'New contact'}</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input className={adminInputClass} value={contactForm.first_name} onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })} placeholder="First name" />
                  <Input className={adminInputClass} value={contactForm.last_name} onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })} placeholder="Last name" />
                  <Input className={adminInputClass} value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Email" />
                  <Input className={adminInputClass} value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="Phone" />
                  <Input className={adminInputClass} value={contactForm.tags} onChange={(e) => setContactForm({ ...contactForm, tags: e.target.value })} placeholder="Tags comma-separated" />
                  <Input className={adminInputClass} value={contactForm.birthday_day} onChange={(e) => setContactForm({ ...contactForm, birthday_day: e.target.value })} placeholder="Birthday day" type="number" />
                  <Input className={adminInputClass} value={contactForm.birthday_month} onChange={(e) => setContactForm({ ...contactForm, birthday_month: e.target.value })} placeholder="Birthday month" type="number" />
                  <Input className={adminInputClass} value={contactForm.birthday_year} onChange={(e) => setContactForm({ ...contactForm, birthday_year: e.target.value })} placeholder="Birthday year optional" type="number" />
                  <Input className={adminInputClass} value={contactForm.nameday_name} onChange={(e) => setContactForm({ ...contactForm, nameday_name: e.target.value })} placeholder="Nameday name" />
                  <Button type="button" variant="outline" className="border-white/20" onClick={lookupContactNameday}>Lookup nameday</Button>
                  <Input className={adminInputClass} value={contactForm.nameday_day} onChange={(e) => setContactForm({ ...contactForm, nameday_day: e.target.value })} placeholder="Nameday day" type="number" />
                  <Input className={adminInputClass} value={contactForm.nameday_month} onChange={(e) => setContactForm({ ...contactForm, nameday_month: e.target.value })} placeholder="Nameday month" type="number" />
                  <label className="flex items-center gap-2 text-white text-sm"><input type="checkbox" checked={contactForm.nameday_confirmed} onChange={(e) => setContactForm({ ...contactForm, nameday_confirmed: e.target.checked })} />Nameday confirmed</label>
                </div>
                <textarea className={adminTextareaClass} value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} placeholder="Notes" />
                <div className="flex flex-wrap gap-4 text-sm text-gray-300">
                  {['email_consent', 'sms_consent', 'marketing_consent'].map((field) => (
                    <label key={field} className="flex items-center gap-2"><input type="checkbox" checked={contactForm[field]} onChange={(e) => setContactForm({ ...contactForm, [field]: e.target.checked })} />{field}</label>
                  ))}
                </div>
                <Button type="submit" className="bg-primary hover:bg-primary/90">Save Contact</Button>
              </form>
              <CardsList items={contacts} empty="No contacts yet" render={(contact) => (
                <div key={contact.id} className="bg-misel-card border border-white/10 rounded-sm p-4 flex justify-between gap-4">
                  <div><p className="text-white">{recipientLabel(contact)}</p><p className="text-sm text-gray-500">{contact.email || '-'} · {contact.phone || '-'} · {(contact.tags || []).join(', ')}</p></div>
                  <Button size="sm" variant="ghost" onClick={() => setContactForm({ ...emptyContact, ...contact, tags: (contact.tags || []).join(', ') })}><Edit size={16} /></Button>
                </div>
              )} />
            </div>
          )}

          {activeTab === 'import' && (
            <div className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2"><Upload size={18} />Import Contacts</h2>
              <textarea className={adminTextareaClass} value={importText} onChange={(e) => setImportText(e.target.value)} placeholder="Paste CSV text here. Greek headers are supported." />
              <div className="flex gap-3">
                <Button onClick={previewImport} variant="outline" className="border-white/20">Preview Import</Button>
                <Button onClick={commitImport} disabled={!importPreview} className="bg-primary hover:bg-primary/90">Commit Import</Button>
              </div>
              {importPreview && <SummaryPanel data={importPreview} />}
            </div>
          )}

          {activeTab === 'direct' && (
            <form onSubmit={sendDirect} className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-5">
              <h2 className="text-xl font-bold text-white">Direct Message</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select value={directForm.recipient_type} onValueChange={(recipient_type) => setDirectForm({ ...directForm, recipient_type })}>
                  <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
                  <SelectContent className={adminSelectContentClass}>
                    <SelectItem className={adminSelectItemClass} value="user">User</SelectItem>
                    <SelectItem className={adminSelectItemClass} value="contact">Contact</SelectItem>
                  </SelectContent>
                </Select>
                {directForm.recipient_type === 'user' ? (
                  <Select value={directForm.user_id || undefined} onValueChange={(user_id) => setDirectForm({ ...directForm, user_id })}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>{users.map((u) => <SelectItem className={adminSelectItemClass} key={u.id} value={u.id}>{recipientLabel(u)}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Select value={directForm.contact_id || undefined} onValueChange={(contact_id) => setDirectForm({ ...directForm, contact_id })}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue placeholder="Select contact" /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>{contacts.map((c) => <SelectItem className={adminSelectItemClass} key={c.id} value={c.id}>{recipientLabel(c)}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <MessageFields value={directForm} setValue={setDirectForm} smsParts={directSms} {...messageFieldProps} />
              <Button type="submit" disabled={(directForm.channel === 'sms' && smsUnavailable) || (directForm.channel === 'email' && emailUnavailable)} className="bg-primary hover:bg-primary/90">
                <Send size={16} className="mr-2" />Send Direct Message
              </Button>
            </form>
          )}

          {activeTab === 'suppressions' && (
            <div className="space-y-6">
              <form onSubmit={saveSuppression} className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><Shield size={18} />Suppression List</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Select value={suppressionForm.channel} onValueChange={(channel) => setSuppressionForm({ ...suppressionForm, channel })}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>{['all', 'email', 'sms'].map((v) => <SelectItem className={adminSelectItemClass} key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className={adminInputClass} value={suppressionForm.email} onChange={(e) => setSuppressionForm({ ...suppressionForm, email: e.target.value })} placeholder="Email" />
                  <Input className={adminInputClass} value={suppressionForm.phone} onChange={(e) => setSuppressionForm({ ...suppressionForm, phone: e.target.value })} placeholder="Phone" />
                  <Input className={adminInputClass} value={suppressionForm.reason} onChange={(e) => setSuppressionForm({ ...suppressionForm, reason: e.target.value })} placeholder="Reason" />
                </div>
                <Button type="submit" className="bg-primary hover:bg-primary/90">Add Suppression</Button>
              </form>
              <CardsList items={suppressions} empty="No suppressions" render={(row) => (
                <div key={row.id} className="bg-misel-card border border-white/10 rounded-sm p-4 text-gray-300">{row.channel} · {row.email || row.phone || row.user_id || row.contact_id} · {row.reason || '-'}</div>
              )} />
            </div>
          )}

          {activeTab === 'namedays' && (
            <div className="space-y-6">
              <form onSubmit={saveNameday} className="bg-misel-card border border-white/10 rounded-sm p-6 space-y-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><Gift size={18} />Namedays</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input className={adminInputClass} value={namedayForm.canonical_name} onChange={(e) => setNamedayForm({ ...namedayForm, canonical_name: e.target.value })} placeholder="Canonical name" />
                  <Input className={adminInputClass} value={namedayForm.names} onChange={(e) => setNamedayForm({ ...namedayForm, names: e.target.value })} placeholder="Names / aliases comma-separated" />
                  <Select value={namedayForm.rule_type} onValueChange={(rule_type) => setNamedayForm({ ...namedayForm, rule_type })}>
                    <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
                    <SelectContent className={adminSelectContentClass}>
                      {['fixed_date', 'orthodox_easter_offset', 'saint_george_greek_rule', 'first_sunday_after_date', 'sunday_after_fixed_date'].map((rule) => <SelectItem className={adminSelectItemClass} key={rule} value={rule}>{rule}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className={adminInputClass} value={namedayForm.easter_offset} onChange={(e) => setNamedayForm({ ...namedayForm, easter_offset: e.target.value })} placeholder="Easter offset" type="number" />
                  <Input className={adminInputClass} value={namedayForm.month} onChange={(e) => setNamedayForm({ ...namedayForm, month: e.target.value })} placeholder="Month" type="number" />
                  <Input className={adminInputClass} value={namedayForm.day} onChange={(e) => setNamedayForm({ ...namedayForm, day: e.target.value })} placeholder="Day" type="number" />
                  <Input className={adminInputClass} value={namedayForm.label_el} onChange={(e) => setNamedayForm({ ...namedayForm, label_el: e.target.value })} placeholder="Greek label" />
                  <Input className={adminInputClass} value={namedayForm.label_en} onChange={(e) => setNamedayForm({ ...namedayForm, label_en: e.target.value })} placeholder="English label" />
                </div>
                <Button type="submit" className="bg-primary hover:bg-primary/90">Save Nameday</Button>
              </form>
              <div className="bg-misel-card border border-white/10 rounded-sm p-4 flex gap-3">
                <Input className={adminInputClass} value={namedaySearch} onChange={(e) => setNamedaySearch(e.target.value)} placeholder="Search name, e.g. Maria" />
                <Button onClick={searchNamedays} variant="outline" className="border-white/20"><Search size={16} /></Button>
                <Button onClick={seedStarterNamedays} variant="outline" className="border-white/20">Seed Greek namedays rules</Button>
                <Button onClick={seedDatasetNamedays} variant="outline" className="border-white/20">Seed bundled open dataset</Button>
                <Button onClick={importMasterNamedays} variant="outline" className="border-white/20">Import master nameday data</Button>
              </div>
              <p className="text-xs text-yellow-200 bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-3">
                Fixed nameday corpus is still extendable through licensed/admin import.
              </p>
              {namedaySearchResult && <NamedaySearchResultPanel data={namedaySearchResult} />}
              <CardsList items={namedays} empty="No namedays imported yet" render={(row) => (
                <div key={row.id} className="bg-misel-card border border-white/10 rounded-sm p-4 flex justify-between gap-4">
                  <div><p className="text-white">{row.label_el || row.canonical_name}</p><p className="text-sm text-gray-500">{row.rule_type} · {row.month || '-'} / {row.day || '-'} · {(row.names || []).join(', ')}</p></div>
                  <Button size="sm" variant="ghost" onClick={() => setNamedayForm({ ...emptyNameday, ...row, names: (row.names || []).join(', ') })}><Edit size={16} /></Button>
                </div>
              )} />
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="bg-misel-card border border-white/10 rounded-sm p-6">
              <h2 className="text-xl font-bold text-white mb-4">Delivery Logs</h2>
              <LogsTable logs={logs} getStatusBadge={getStatusBadge} />
            </div>
          )}
        </>
      )}

      {preview && (
        <ModalPanel title={`Preview: ${preview.title}`} onClose={() => setPreview(null)}>
          <SummaryPanel data={preview} />
        </ModalPanel>
      )}
      {selectedLogs && (
        <ModalPanel title={`Logs: ${selectedLogs.title}`} onClose={() => setSelectedLogs(null)}>
          <LogsTable logs={selectedLogs.logs || []} getStatusBadge={getStatusBadge} />
        </ModalPanel>
      )}
      {runDueResult && (
        <ModalPanel title={runDueResult.title || 'Run Due results'} onClose={() => setRunDueResult(null)}>
          <RunDueResultPanel result={runDueResult} />
        </ModalPanel>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, title }) {
  return (
    <div className="text-center py-12 bg-misel-card border border-white/10 rounded-sm">
      <Icon className="mx-auto mb-4 text-gray-500" size={42} />
      <p className="text-gray-400">{title}</p>
    </div>
  );
}

function CardsList({ items, empty, render }) {
  if (!items || items.length === 0) {
    return <EmptyState icon={FileText} title={empty} />;
  }
  return <div className="space-y-3">{items.map(render)}</div>;
}

function SummaryPanel({ data }) {
  return (
    <pre className="bg-black/40 border border-white/10 rounded-sm p-4 text-xs text-gray-200 overflow-auto max-h-96">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

function NamedaySearchResultPanel({ data }) {
  const options = data?.options || [];
  if (!options.length) return <SummaryPanel data={data} />;
  return (
    <div className="bg-black/30 border border-white/10 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-white/10">
            <th className="py-2 px-3">Name</th>
            <th className="py-2 px-3">Aliases</th>
            <th className="py-2 px-3">Calculated date</th>
            <th className="py-2 px-3">Rule</th>
            <th className="py-2 px-3">Type</th>
            <th className="py-2 px-3">Feast/context</th>
            <th className="py-2 px-3">Source</th>
          </tr>
        </thead>
        <tbody>
          {options.map((option) => (
            <tr key={`${option.id}-${option.date}`} className="border-b border-white/5 text-gray-300">
              <td className="py-2 px-3">{option.canonical_name || option.label_el}</td>
              <td className="py-2 px-3 text-xs">{(option.display_aliases || []).join(', ')}</td>
              <td className="py-2 px-3">{option.calculated_date || option.date}</td>
              <td className="py-2 px-3">{option.rule_type || '-'}</td>
              <td className="py-2 px-3">{option.record_type || '-'}</td>
              <td className="py-2 px-3">{option.feast_context || option.label_el || '-'}</td>
              <td className="py-2 px-3">{option.source || option.source_name || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RunDueResultPanel({ result }) {
  const rows = result?.results || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-black/30 border border-white/10 rounded-sm p-4">
          <p className="text-xs text-gray-500">Processed</p>
          <p className="text-2xl font-bold text-white">{result?.processed || 0}</p>
        </div>
        <div className="bg-black/30 border border-white/10 rounded-sm p-4">
          <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Clock size={12} />Due</p>
          <p className="text-2xl font-bold text-white">{result?.due_count ?? rows.length}</p>
        </div>
        <div className="bg-black/30 border border-white/10 rounded-sm p-4">
          <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Users size={12} />Candidates</p>
          <p className="text-2xl font-bold text-white">{result?.candidate_count ?? '-'}</p>
        </div>
        <div className="bg-black/30 border border-white/10 rounded-sm p-4">
          <p className="text-xs text-gray-500">Status</p>
          <p className="text-sm text-gray-200">{result?.message || 'Run completed'}</p>
        </div>
      </div>
      {(result?.processed || 0) === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-sm p-4 text-sm">
          No due campaigns were processed. Check status=scheduled/partial and scheduled_at &lt;= now.
        </div>
      )}
      {rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-white/10">
                <th className="py-2">Campaign</th>
                <th className="py-2">Sent</th>
                <th className="py-2">Failed</th>
                <th className="py-2">Skipped</th>
                <th className="py-2">Recipients</th>
                <th className="py-2">Eligible</th>
                <th className="py-2">Remaining</th>
                <th className="py-2">Stopped</th>
                <th className="py-2">Skipped reasons</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.campaign_id} className="border-b border-white/5 text-gray-300">
                  <td className="py-2">{row.campaign_id}</td>
                  <td className="py-2 text-green-300">{row.sent || 0}</td>
                  <td className="py-2 text-red-300">{row.failed || 0}</td>
                  <td className="py-2">{row.skipped || 0}</td>
                  <td className="py-2">{row.recipients || 0}</td>
                  <td className="py-2">{row.eligible_count || 0}</td>
                  <td className="py-2">{row.remaining_count || 0}</td>
                  <td className="py-2">{row.stopped_reason || '-'}</td>
                  <td className="py-2 text-xs">{row.skipped_by_reason ? JSON.stringify(row.skipped_by_reason) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <SummaryPanel data={result} />
      )}
    </div>
  );
}

function ModalPanel({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative z-[9999] bg-misel-card border border-white/10 rounded-sm max-w-5xl w-full max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X size={18} /></Button>
        </div>
        {children}
      </div>
    </div>
  );
}

function LogsTable({ logs, getStatusBadge }) {
  if (!logs || logs.length === 0) return <p className="text-gray-500">No delivery logs yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-white/10">
            <th className="py-2">Recipient</th>
            <th className="py-2">Channel</th>
            <th className="py-2">Status</th>
            <th className="py-2">Provider</th>
            <th className="py-2">Provider Status</th>
            <th className="py-2">Error Code</th>
            <th className="py-2">Skipped</th>
            <th className="py-2">Sent</th>
            <th className="py-2">Error</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="border-b border-white/5 text-gray-300">
              <td className="py-2">{log.email || log.phone || log.user_id || log.contact_id || '-'}</td>
              <td className="py-2">{log.channel}</td>
              <td className="py-2">{getStatusBadge(log.status)}</td>
              <td className="py-2">{log.provider || '-'}</td>
              <td className="py-2">{log.provider_status || '-'}</td>
              <td className="py-2">{log.provider_error_code || '-'}</td>
              <td className="py-2">{log.skipped_reason || '-'}</td>
              <td className="py-2">{log.sent_at ? new Date(log.sent_at).toLocaleString('el-GR') : '-'}</td>
              <td className="py-2 text-red-300">{log.error || log.provider_error_message || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
